import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CertificationFeature } from '../../models/program/certification-feature.interface';
import { Observable } from 'rxjs';
import { Program } from '../../models/application/program.interface';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';
import { EducationalAccessProgramProvider } from '../../models/program/educational-access-program-provider.interface';
import {
	ApplicationDocumentType,
	ContentItem,
	ProgramAppealConfig,
	ProgramFeatureReference,
	ProgramLoanFeature,
	ProgramType,
	User
} from '@edassist/api';
import { withCache } from '@ngneat/cashew';
import { constants, LocalStorageService } from '@edassist/common';

@Injectable({ providedIn: 'root' })
export class ProgramService {
	constructor(private readonly http: HttpClient) {}

	// TODO: either make this a generic method that doesn't return a specific feature type or make it certification specific
	getProgramFeatures(programId: number, feature: string): Observable<CertificationFeature> {
		return this.http.get<CertificationFeature>(`programs/${programId.toString()}/features/${feature}`);
	}

	isProgramFeatureEnabled(program: Program, featureCode: string): boolean {
		return program?.programFeaturesReferences
			.map((featureReference) => featureReference.programFeature)
			.some((feature) => feature.code === featureCode);
	}

	isAnyProgramFeatureEnabled(programs: Array<Program>, featureCode: string): boolean {
		return !!programs?.find((program) => this.isProgramFeatureEnabled(program, featureCode));
	}

	disableAppClosingJobFeature(programId: number): Observable<void> {
		return this.http.delete<void>(`programs/${programId}/features/payment-app-closing`);
	}

	getProgramId(): number | null {
		const user: User = LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER_HYBRID)?.rulesEligibility;

		return user?.programs?.[0]?.id || null;
	}

	getProgram(): User {
		const user: User = LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER_HYBRID)?.rulesEligibility;

		return user?.programs?.[0] || null;
	}

	setSelectedProgram(program: Program): void {
		LocalStorageService.setItem(constants.localStorageKeys.CURRENT_USER_SELECTED_PROGRAM, program);
	}

	getSelectedProgram(): Program | null {
		return LocalStorageService.getItem(constants.localStorageKeys.CURRENT_USER_SELECTED_PROGRAM);
	}

	getEdAccessProgramConfigDetails(programIds: string, featurePath: string): Observable<boolean> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams({
			programs: programIds
		});

		return this.http.get<boolean>(`programs/features/${featurePath}`, queryParams);
	}

	hasSharedCapInfoProgram(programId: number): Observable<boolean> {
		return this.http.get<boolean>(`programs/${programId}/has-shared-cap-program`);
	}

	getEducationAccessProgramProviders(
		programId: number,
		providerId: number
	): Observable<EducationalAccessProgramProvider> {
		return this.http.get<EducationalAccessProgramProvider>(
			`programs/${programId}/education-access/education-providers/${providerId}`,
			{
				context: withCache({
					version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
					key: `getEducationAccessProgramProviders-${programId}-${providerId}`
				})
			}
		);
	}

	getProgramFeatureReference(programId: number, featureCode: string): Observable<ProgramFeatureReference> {
		return this.http.get<ProgramFeatureReference>(`programs/${programId.toString()}/features/${featureCode}`);
	}

	getLoanProgramFeatureReference(programId: number): Observable<ProgramLoanFeature> {
		return this.http.get<ProgramLoanFeature>(`programs/${programId.toString()}/features/loan`);
	}

	getProgramFeatureReferences(programId: number): Observable<Array<ProgramFeatureReference>> {
		return this.http.get<Array<ProgramFeatureReference>>(`programs/${programId.toString()}/features`);
	}

	createProgramFeatureReferences(programId: number, programFeatureCodeList: Array<string>): Observable<void> {
		return this.http.post<void>(`programs/${programId.toString()}/enable-features`, programFeatureCodeList);
	}

	deleteProgramFeatureReferences(programId: number, programFeatureCodes: Array<string>): Observable<void> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams({
			programFeatureCodes
		});

		return this.http.delete<void>(`programs/${programId.toString()}/disable-features`, queryParams);
	}

	getAllProgramTypes(): Observable<Array<ProgramType>> {
		return this.http.get<Array<ProgramType>>('programs/program-types', {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: 'getAllProgramTypes'
			})
		});
	}

	getAppealConfigForProgram(programId: number): Observable<ProgramAppealConfig> {
		return this.http.get<ProgramAppealConfig>(`programs/${programId}/features/appeal`);
	}

	getRelatedAgreements(programId: number): Observable<Array<ContentItem>> {
		return this.http.get<Array<ContentItem>>(`programs/${programId}/related-agreements`);
	}

	getEligibleDocumentTypesByProgramId(programId: number): Observable<Array<ApplicationDocumentType>> {
		return this.http.get<Array<ApplicationDocumentType>>(`programs/${programId.toString()}/documents`);
	}

	getActiveSelectedProgram(programId: number): Observable<Program> {
		return this.http.get<Program>(`programs/${programId}`);
	}

	isProgramNonDegree(programId: number): Observable<boolean> {
		return this.http.get<boolean>(`programs/${programId}/is-non-degree`);
	}
}
